import { Contact, IContact } from '@app/models/Contact'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IUsersListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IContact>
  readonly totalItems: number
}

const UsersListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Contact>(),
  totalItems: 0,
})

export class UsersListState extends UsersListStateRecord
  implements IUsersListState {
  public static fromJS(initialState?: Partial<IUsersListState>) {
    if (!initialState) {
      return new UsersListState()
    }

    const { items, ...rest } = initialState
    return new UsersListState({
      items: items ? List(items.map((i) => Contact.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Contact>
  public readonly totalItems: number

  constructor(props?: Partial<UsersListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum UsersListActionTypes {
  LoadUsers = 'USERS_LIST::LOAD_USERS',
  LoadUsersFailure = 'USERS_LIST::LOAD_USERS_FAILURE',
  LoadUsersRequest = 'USERS_LIST::LOAD_USERS_REQUEST',
  LoadUsersSuccess = 'USERS_LIST::LOAD_USERS_SUCCESS',
  SetConnectionType = 'USERS_LIST::SET_CONNECTION_TYPE',
  SetFilterText = 'USERS_LIST::SET_FILTER_TEXT',
  SetScope = 'USERS_LIST::SET_SCOPE',
  SetSortOrder = 'USERS_LIST::SET_SORT_ORDER',
}

interface ILoadUsersAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: UsersListActionTypes.LoadUsers
}

interface ILoadUsersFailureAction {
  error: string
  type: UsersListActionTypes.LoadUsersFailure
}

interface ILoadUsersRequestAction {
  type: UsersListActionTypes.LoadUsersRequest
}

interface ILoadUsersSuccessAction {
  append: boolean
  items: Contact[]
  totalItems: number
  type: UsersListActionTypes.LoadUsersSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: UsersListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadUsersFailureAction
  | ILoadUsersRequestAction
  | ILoadUsersSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadUsers(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadUsersAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: UsersListActionTypes.LoadUsers,
  }
}

function loadUsersFailure(error: string): ILoadUsersFailureAction {
  return {
    error,
    type: UsersListActionTypes.LoadUsersFailure,
  }
}

function loadUsersRequest(): ILoadUsersRequestAction {
  return {
    type: UsersListActionTypes.LoadUsersRequest,
  }
}

function loadUsersSuccess(
  items: Contact[],
  append: boolean,
  totalItems: number,
): ILoadUsersSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: UsersListActionTypes.LoadUsersSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: UsersListActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadUsers,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<UsersListState> = (
  state: UsersListState = new UsersListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case UsersListActionTypes.LoadUsersFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as UsersListState

    case UsersListActionTypes.LoadUsersRequest:
      return state.set('isLoading', true) as UsersListState

    case UsersListActionTypes.LoadUsersSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Contact>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as UsersListState

    case UsersListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as UsersListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadUsersSaga(action: ILoadUsersAction) {
  try {
    yield put(loadUsersRequest())

    const currentState: UsersListState = yield select(
      (state: ApplicationState) => state.usersList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    query.push(`sort=Name`)

    const baseUrl = 'api/Users'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Contact[] = result.results.map((c: any) =>
        Contact.fromJS(c),
      )
      yield put(loadUsersSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadUsersFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadUsersFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadUsers() {
  yield takeLatest(UsersListActionTypes.LoadUsers, loadUsersSaga)
}

export function* usersListRootSaga() {
  yield fork(watchLoadUsers)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): UsersListState =>
  state.usersList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
